import React from "react";
import { Clock, Briefcase, ShieldCheck, ArrowUpRight, Users } from "lucide-react";
import Banner from "../../../Components/Common/Banner";
import { useNavigate } from "react-router-dom";

const PROGRAMS = [
  {
    id: "clock-stopper",
    name: "Clock Stopper",
    tagline: "Unpaid internships, real-world reps",
    description:
      "Hands-on internships built for fresh graduates. Mentorship, live projects, and structured training to close the gap between classroom and career.",
    stats: [
      { label: "Graduates trained", value: "500+" },
      { label: "Success rate", value: "85%" },
    ],
    icon: Clock,
    accent: "emerald",
    href: "/knowledge/program/career-explorer/clock-stopper",
  },
  {
    id: "hands-on-learn",
    name: "Hands-On Learn",
    tagline: "Paid, full-time training for STEM talent",
    description:
      "Full-time, paid training built for STEM students. Practical experience and comprehensive mentorship that turns coursework into industry-ready skill.",
    stats: [
      { label: "Focus", value: "STEM" },
      { label: "Track", value: "Full-time" },
    ],
    icon: Briefcase,
    accent: "emerald",
    href: "/knowledge/program/career-explorer/hands-on-learn",
  },
  {
    id: "employshield",
    name: "EmployShield",
    tagline: "Protect and verify your work history",
    description:
      "Secure storage, verification, and management of your employment records, so your professional history stays accurate, credible, and easy to prove.",
    stats: [
      { label: "Coverage", value: "Full history" },
      { label: "Access", value: "Secure" },
    ],
    icon: ShieldCheck,
    accent: "emerald",
    href: "/knowledge/program/career-explorer/hands-on-learn",
  },
];

const ACCENTS = {
  blue: {
    iconBg: "bg-blue-50",
    iconText: "text-blue-600",
    ring: "group-hover:ring-blue-200",
    bar: "bg-blue-500",
    btn: "bg-blue-600 hover:bg-blue-700 focus-visible:outline-blue-600",
    tag: "text-blue-700 bg-blue-50",
  },
  emerald: {
    iconBg: "bg-emerald-50",
    iconText: "text-emerald-600",
    ring: "group-hover:ring-emerald-200",
    bar: "bg-emerald-500",
    btn: "bg-emerald-600 hover:bg-emerald-700 focus-visible:outline-emerald-600",
    tag: "text-emerald-700 bg-emerald-50",
  },
  violet: {
    iconBg: "bg-violet-50",
    iconText: "text-violet-600",
    ring: "group-hover:ring-violet-200",
    bar: "bg-violet-500",
    btn: "bg-violet-600 hover:bg-violet-700 focus-visible:outline-violet-600",
    tag: "text-violet-700 bg-violet-50",
  },
};

function ProgramCard({ program }) {
  const a = ACCENTS[program.accent];
  const Icon = program.icon;
  const navigate  = useNavigate();
  return (
    <div
      className={`group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 ring-1 ring-transparent transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${a.ring}`}
    >
      <span className={`absolute inset-x-0 top-0 h-1 rounded-t-2xl ${a.bar}`} />

      <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${a.iconBg}`}>
        <Icon className={`h-6 w-6 ${a.iconText}`} strokeWidth={2} />
      </div>

      <h3 className="mt-5 text-xl font-bold text-slate-900">{program.name}</h3>
      <p className={`mt-1 text-sm font-medium ${a.iconText}`}>{program.tagline}</p>

      <p className="mt-3 text-sm leading-relaxed text-slate-600">{program.description}</p>

      <div className="mt-5 flex gap-2">
        {program.stats.map((s) => (
          <span
            key={s.label}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${a.tag}`}
          >
            {s.value}
            <span className="font-normal text-slate-500">{s.label}</span>
          </span>
        ))}
      </div>

      <button    
        onClick={()=>{navigate(program.href)
            window.scrollTo({
                top:0,
                behavior :"smooth"
            })
        }}
        className={`mt-6 inline-flex w-fit items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${a.btn}`}
      >
        Explore more
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </div>
  );
}

export default function CareerExplorer() {
  return (
    <div className="min-h-screen w-full bg-white">
        <Banner pageName="Career Explorer" breadcrumbs="Home / Programs / Career Explorer" />
      <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        {/* Hero */}
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
            <Users className="h-3.5 w-3.5" />
            Career Programs
          </span>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
            Career Explorer
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            One place to find the program that fits where you are right now: internships to build
            experience, paid STEM training to grow a skill set, or tools to protect the work history
            you've already built.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROGRAMS.map((program) => (
            <ProgramCard key={program.id} program={program} />
          ))}
        </div>

       
      </div>
    </div>
  );
}